import { ChangeEvent } from 'react';
import {
  Alert,
  Box,
  Chip,
  Icon,
  Input,
  Text,
  Title,
} from '@nimbus-ds/components';
import { SearchIcon } from '@nimbus-ds/icons';
import { SelectedProductInterface } from 'domains/Catalog/Products/components';
import useTranslationCatalog from 'domains/Catalog/useTranslationCatalog';

interface SelectProductModalHeaderProps {
  searchValue: string;
  selectedCount: number;
  excludedProductsCount: number;
  products?: SelectedProductInterface[] | null;
  onSearchChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onSearch: () => void;
  onClearSelection: () => void;
}

export function SelectProductModalHeader({
  searchValue,
  selectedCount,
  excludedProductsCount,
  products,
  onSearchChange,
  onSearch,
  onClearSelection,
}: Readonly<SelectProductModalHeaderProps>): JSX.Element {
  const t = useTranslationCatalog();

  const title = t('subscriptions.form.products.modalTitle');
  const description = t('subscriptions.form.products.description');
  const searchPlaceholder = t('subscriptions.form.products.searchPlaceholder');

  const showSearchSection = !(searchValue === '' && products?.length === 0);

  const counterText = t('subscriptions.form.products.selected', {
    count: selectedCount,
  });

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="stretch"
      gap="2"
      paddingX="4"
    >
      <Title as="h2">{title}</Title>

      <Text color="neutral-textLow">{description}</Text>

      {showSearchSection && (
        <Box display="flex" flexDirection="column" alignItems="stretch" gap="2">
          <Box>
            <Input
              type="search"
              name="product"
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={onSearchChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                  onSearch();
                }
              }}
              append={<Icon source={<SearchIcon />} />}
              appendPosition="start"
            />
          </Box>
          {selectedCount > 0 && (
            <Box>
              <Chip
                id="SelectProductModalChip"
                text={counterText}
                removable
                onClick={onClearSelection}
              />
            </Box>
          )}
          {excludedProductsCount > 0 && (
            <Box>
              <Alert appearance="neutral">
                {t('subscriptions.form.products.info')}
              </Alert>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
